import React, { useEffect, useRef, useState } from 'react';
import {
	SypacCheckbox,
	SypacInput,
	SypacText,
} from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import { CompanyService } from '../../../../services/company.services';
import useDebounce from '../../../../hooks/useDebounce';
import { useGetCompanyUsers } from '../../../../hooks/use-get-company-users';
import ProducerCompanyIcon from '../../../../assets/ProducerCompanyIcon';
import { ChevronRightIcon } from '../../../../assets/ChevronRightIcon';
import { Person } from '../../../../assets/Person';
import {
	UsersInterface,
	UserStatus,
} from '../../../../interfaces/Users.interface';
import { cn } from '../../../../lib/utils';
import { SectionProps } from '../addNewContract.interface';
import Flag from 'react-world-flags';
import { GlobeIcon } from '../../../../assets/GlobeIcon';

type CompanyShort = {
	id: number | string;
	name: string;
	countryCode?: string;
	logoUrl?: string;
	registryAddress?: string;
};

function useOutsideClickCloser(
	pairs: Array<{
		inputRef: React.RefObject<HTMLElement>;
		menuRef?: React.RefObject<HTMLElement>;
		close: () => void;
	}>,
) {
	useEffect(() => {
		const onDocMouseDown = (e: MouseEvent) => {
			const target = e.target as Node;
			pairs.forEach(({ inputRef, menuRef, close }) => {
				const onInput = !!inputRef.current?.contains(target);
				const inMenu = !!menuRef?.current?.contains(target);
				if (!onInput && !inMenu) close();
			});
		};
		document.addEventListener('mousedown', onDocMouseDown);
		return () => document.removeEventListener('mousedown', onDocMouseDown);
	}, [pairs]);
}

async function fetchCompaniesSimple(params: {
	search: string;
	companyTypes: Array<'customer' | 'producer'>;
	countryCode?: string;
}) {
	if (!params.search.trim()) {
		return {
			items: [] as Array<{ id: string; name: string; countryCode?: string }>,
		};
	}
	const { data } = await CompanyService.getCompanies({
		limit: 100,
		status: 'verified',
		companyTypes: params.companyTypes,
		search: params.search,
		countryCode: params.countryCode,
	});

	const items: Array<{
		id: string;
		name: string;
		countryCode?: string;
		logoUrl?: string;
		registryAddress?: string;
	}> = (data?.items ?? []).map((c: CompanyShort) => ({
		id: String(c.id),
		name: c.name,
		countryCode: c.countryCode,
		logoUrl: c.logoUrl,
		registryAddress: c.registryAddress,
	}));

	return { items };
}

const CONTRACT_COUNTRIES = [
	{
		code: 'PL',
		flagCode: 'PL',
		labelKey: 'adminProducts.poland',
		fallbackLabel: 'Poland',
	},
	{
		code: 'MD',
		flagCode: 'MD',
		labelKey: 'adminProducts.moldova',
		fallbackLabel: 'Moldova',
	},
];

const PartiesSection: React.FC<SectionProps> = ({ formik }) => {
	const { t } = useTranslate();

	const [buyerDisplay, setBuyerDisplay] = useState<string>('');
	const [sellerDisplay, setSellerDisplay] = useState<string>('');
	const [buyerOptions, setBuyerOptions] = useState<
		Array<{
			id: string;
			name: string;
			countryCode?: string;
			logoUrl?: string;
			registryAddress?: string;
		}>
	>([]);
	const [sellerOptions, setSellerOptions] = useState<
		Array<{
			id: string;
			name: string;
			countryCode?: string;
			logoUrl?: string;
			registryAddress?: string;
		}>
	>([]);
	const [showBuyerOptions, setShowBuyerOptions] = useState<boolean>(false);
	const [showSellerOptions, setShowSellerOptions] = useState<boolean>(false);
	const [showCountryOptions, setShowCountryOptions] = useState<boolean>(false);

	const buyerWrapperRef = useRef<HTMLDivElement | null>(null);
	const sellerWrapperRef = useRef<HTMLDivElement | null>(null);
	const debouncedBuyer = useDebounce(buyerDisplay, 500);
	const debouncedSeller = useDebounce(sellerDisplay, 500);

	const [showBuyerReps, setShowBuyerReps] = useState<boolean>(false);
	const [showSellerReps, setShowSellerReps] = useState<boolean>(false);
	const [buyerCompanyUsers, setBuyerCompanyUsers] = useState<UsersInterface[]>(
		[],
	);
	const [sellerCompanyUsers, setSellerCompanyUsers] = useState<
		UsersInterface[]
	>([]);

	const buyerRepInputRef = useRef<HTMLInputElement | null>(null);
	const buyerRepMenuRef = useRef<HTMLDivElement | null>(null);
	const sellerRepInputRef = useRef<HTMLInputElement | null>(null);
	const sellerRepMenuRef = useRef<HTMLDivElement | null>(null);
	const buyerCompanyInputRef = useRef<HTMLInputElement | null>(null);
	const buyerOptionsMenuRef = useRef<HTMLDivElement | null>(null);
	const sellerCompanyInputRef = useRef<HTMLInputElement | null>(null);
	const sellerOptionsMenuRef = useRef<HTMLDivElement | null>(null);
	const countryInputRef = useRef<HTMLInputElement | null>(null);
	const countryOptionsMenuRef = useRef<HTMLDivElement | null>(null);
	const countryWrapperRef = useRef<HTMLDivElement | null>(null);

	const isOwner = (u: UsersInterface) => u.role === 'owner';

	useOutsideClickCloser([
		{
			inputRef: countryWrapperRef,
			menuRef: countryOptionsMenuRef,
			close: () => setShowCountryOptions(false),
		},
		{
			inputRef: buyerRepInputRef,
			menuRef: buyerRepMenuRef,
			close: () => setShowBuyerReps(false),
		},
		{
			inputRef: sellerRepInputRef,
			menuRef: sellerRepMenuRef,
			close: () => setShowSellerReps(false),
		},
		{
			inputRef: buyerCompanyInputRef,
			menuRef: buyerOptionsMenuRef,
			close: () => setShowBuyerOptions(false),
		},
		{
			inputRef: sellerCompanyInputRef,
			menuRef: sellerOptionsMenuRef,
			close: () => setShowSellerOptions(false),
		},
	]);

	const [buyerUsersResponse] = useGetCompanyUsers({
		companyId: +formik.values.buyerCompany,
	});
	const [sellerUsersResponse] = useGetCompanyUsers({
		companyId: +formik.values.sellerCompany,
	});

	useEffect(() => {
		const users =
			(Array.isArray(buyerUsersResponse)
				? buyerUsersResponse
				: buyerUsersResponse?.items) ?? [];

		setBuyerCompanyUsers(
			users.filter((u) => u?.status !== UserStatus.DISABLED),
		);
	}, [buyerUsersResponse]);

	useEffect(() => {
		const users =
			(Array.isArray(sellerUsersResponse)
				? sellerUsersResponse
				: sellerUsersResponse?.items) ?? [];

		setSellerCompanyUsers(
			users.filter((u) => u?.status !== UserStatus.DISABLED),
		);
	}, [sellerUsersResponse]);

	useEffect(() => {
		let live = true;
		(async () => {
			if (formik.values.buyerCompany) {
				setShowBuyerOptions(false);
				return;
			}

			if (!buyerDisplay.trim()) {
				setBuyerOptions([]);
				setShowBuyerOptions(false);
				return;
			}

			if (!debouncedBuyer.trim()) {
				setBuyerOptions([]);
				setShowBuyerOptions(false);
				return;
			}

			const res = await fetchCompaniesSimple({
				search: debouncedBuyer,
				companyTypes: ['customer'],
				countryCode: formik.values.countryCode || undefined,
			});
			if (!live) return;
			setBuyerOptions(res.items || []);
			setShowBuyerOptions(true);
		})();
		return () => {
			live = false;
		};
	}, [
		debouncedBuyer,
		buyerDisplay,
		formik.values.buyerCompany,
		formik.values.countryCode,
	]);

	useEffect(() => {
		let live = true;
		(async () => {
			if (formik.values.sellerCompany) {
				setShowSellerOptions(false);
				return;
			}

			if (!sellerDisplay.trim()) {
				setSellerOptions([]);
				setShowSellerOptions(false);
				return;
			}

			if (!debouncedSeller.trim()) {
				setSellerOptions([]);
				setShowSellerOptions(false);
				return;
			}

			const res = await fetchCompaniesSimple({
				search: debouncedSeller,
				companyTypes: ['producer'],
				countryCode: formik.values.countryCode || undefined,
			});

			if (!live) return;
			setSellerOptions(res.items || []);
			setShowSellerOptions(true);
		})();
		return () => {
			live = false;
		};
	}, [
		debouncedSeller,
		sellerDisplay,
		formik.values.sellerCompany,
		formik.values.countryCode,
	]);

	useEffect(() => {
		formik.setFieldValue('buyerRepresentativeUid', '');
		formik.setFieldValue('buyerRepresentativeLabel', '');
		formik.setFieldValue('buyerRepresentativePhotoUrl', '');
		setBuyerCompanyUsers([]);
	}, [formik.values.buyerCompany]); // eslint-disable-line

	useEffect(() => {
		formik.setFieldValue('sellerRepresentativeUid', '');
		formik.setFieldValue('sellerRepresentativeLabel', '');
		formik.setFieldValue('sellerRepresentativePhotoUrl', '');
		setSellerCompanyUsers([]);
	}, [formik.values.sellerCompany]); // eslint-disable-line

	useEffect(() => {
		if (!formik.values.buyerCompany) return;
		if (formik.values.buyerRepresentativeUid) return;
		if (!buyerCompanyUsers.length) return;

		const owner = buyerCompanyUsers.find(isOwner) ?? buyerCompanyUsers[0];
		if (owner) {
			formik.setFieldValue('buyerRepresentativeUid', owner.uid);
			formik.setFieldValue(
				'buyerRepresentativeLabel',
				`${owner.firstName} ${owner.lastName}`,
			);
			formik.setFieldValue('buyerRepresentativePhotoUrl', owner.photoUrl || '');

			formik.setFieldTouched('buyerRepresentativeUid', false, false);
			formik.setFieldError('buyerRepresentativeUid', undefined);
		}
	}, [buyerCompanyUsers, formik.values.buyerCompany]); // eslint-disable-line

	useEffect(() => {
		if (!formik.values.sellerCompany) return;
		if (formik.values.sellerRepresentativeUid) return;
		if (!sellerCompanyUsers.length) return;

		const owner = sellerCompanyUsers.find(isOwner) ?? sellerCompanyUsers[0];
		if (owner) {
			formik.setFieldValue('sellerRepresentativeUid', owner.uid);
			formik.setFieldValue(
				'sellerRepresentativeLabel',
				`${owner.firstName} ${owner.lastName}`,
			);
			formik.setFieldValue(
				'sellerRepresentativePhotoUrl',
				owner.photoUrl || '',
			);

			formik.setFieldTouched('sellerRepresentativeUid', false, false);
			formik.setFieldError('sellerRepresentativeUid', undefined);
		}
	}, [sellerCompanyUsers, formik.values.sellerCompany]); // eslint-disable-line

	const handleBuyerInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		setBuyerDisplay(val);
		formik.setFieldValue('buyerCompany', '');
		formik.setFieldValue('buyerLabel', '');
		formik.setFieldValue('buyerCompanyCountryCode', '');
		formik.setFieldValue('buyerCompanyAddress', '');
		formik.setFieldValue('buyerCompanyLogoUrl', '');
		formik.setFieldValue('ignoreCreditLimit', false);
	};

	const handleSellerInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		setSellerDisplay(val);
		formik.setFieldValue('sellerCompany', '');
		formik.setFieldValue('sellerLabel', '');
	};

	const handleBuyerFocusOrClick = () => {
		if (!formik.values.buyerCompany && buyerDisplay.trim().length > 0)
			setShowBuyerOptions(true);
	};

	const handleSellerFocusOrClick = () => {
		if (!formik.values.sellerCompany && sellerDisplay.trim().length > 0)
			setShowSellerOptions(true);
	};

	const pickCountry = (code: string) => {
		if (formik.values.countryCode === code) {
			setShowCountryOptions(false);
			return;
		}

		formik.setFieldValue('countryCode', code);

		formik.setFieldValue('buyerCompany', '');
		formik.setFieldValue('buyerLabel', '');
		formik.setFieldValue('buyerRepresentativeUid', '');
		formik.setFieldValue('buyerRepresentativeLabel', '');
		formik.setFieldValue('buyerCompanyCountryCode', '');
		formik.setFieldValue('buyerCompanyAddress', '');
		formik.setFieldValue('buyerCompanyLogoUrl', '');
		formik.setFieldValue('buyerRepresentativePhotoUrl', '');
		formik.setFieldValue('sellerCompanyLogoUrl', '');
		formik.setFieldValue('sellerRepresentativePhotoUrl', '');
		setBuyerDisplay('');
		setBuyerOptions([]);
		setBuyerCompanyUsers([]);

		formik.setFieldValue('sellerCompany', '');
		formik.setFieldValue('sellerLabel', '');
		formik.setFieldValue('sellerRepresentativeUid', '');
		formik.setFieldValue('sellerRepresentativeLabel', '');
		formik.setFieldValue('ignoreCreditLimit', false);

		setSellerDisplay('');
		setSellerOptions([]);
		setSellerCompanyUsers([]);

		setShowBuyerReps(false);
		setShowSellerReps(false);

		formik.setFieldTouched('buyerRepresentativeUid', false, false);
		formik.setFieldError('buyerRepresentativeUid', undefined);
		formik.setFieldTouched('sellerRepresentativeUid', false, false);
		formik.setFieldError('sellerRepresentativeUid', undefined);

		setShowCountryOptions(false);
	};

	const pickBuyer = (company: {
		id: string;
		name: string;
		countryCode?: string;
		logoUrl?: string;
		registryAddress?: string;
	}) => {
		setBuyerDisplay(company.name);
		formik.setFieldValue('buyerCompany', company.id);
		formik.setFieldValue('buyerLabel', company.name);

		if (!formik.values.countryCode && company.countryCode) {
			formik.setFieldValue('countryCode', company.countryCode);
		}

		formik.setFieldValue('buyerCompanyCountryCode', company.countryCode || '');
		formik.setFieldValue('buyerCompanyAddress', company.registryAddress || '');
		formik.setFieldValue('buyerCompanyLogoUrl', company.logoUrl || '');

		formik.setFieldValue('buyerRepresentativeUid', '');
		formik.setFieldValue('buyerRepresentativeLabel', '');
		formik.setFieldValue('buyerRepresentativePhotoUrl', '');
		formik.setFieldValue('ignoreCreditLimit', false);

		setShowBuyerOptions(false);
		setShowBuyerReps(false);

		formik.setFieldTouched('buyerRepresentativeUid', false, false);
		formik.setFieldError('buyerRepresentativeUid', undefined);
	};

	const pickSeller = (company: {
		id: string;
		name: string;
		countryCode?: string;
		logoUrl?: string;
		registryAddress?: string;
	}) => {
		setSellerDisplay(company.name);
		formik.setFieldValue('sellerCompany', company.id);
		formik.setFieldValue('sellerLabel', company.name);

		formik.setFieldValue('sellerCompanyLogoUrl', company.logoUrl || '');

		formik.setFieldValue('sellerRepresentativeUid', '');
		formik.setFieldValue('sellerRepresentativeLabel', '');
		formik.setFieldValue('sellerRepresentativePhotoUrl', '');

		setShowSellerOptions(false);
		setShowSellerReps(false);

		formik.setFieldTouched('sellerRepresentativeUid', false, false);
		formik.setFieldError('sellerRepresentativeUid', undefined);
	};

	const pickBuyerRep = (u: UsersInterface) => {
		formik.setFieldValue('buyerRepresentativeUid', u.uid);
		formik.setFieldValue(
			'buyerRepresentativeLabel',
			`${u.firstName} ${u.lastName}`,
		);
		formik.setFieldValue('buyerRepresentativePhotoUrl', u.photoUrl || '');
		setShowBuyerReps(false);
	};

	const pickSellerRep = (u: UsersInterface) => {
		formik.setFieldValue('sellerRepresentativeUid', u.uid);
		formik.setFieldValue(
			'sellerRepresentativeLabel',
			`${u.firstName} ${u.lastName}`,
		);
		formik.setFieldValue('sellerRepresentativePhotoUrl', u.photoUrl || '');
		setShowSellerReps(false);
	};

	const resolveBuyerRepName = () => {
		if (formik.values.buyerRepresentativeLabel)
			return formik.values.buyerRepresentativeLabel;
		const byUid = buyerCompanyUsers.find(
			(u) => String(u.uid) === String(formik.values.buyerRepresentativeUid),
		);
		return byUid ? `${byUid.firstName} ${byUid.lastName}` : '';
	};

	const resolveSellerRepName = () => {
		if (formik.values.sellerRepresentativeLabel)
			return formik.values.sellerRepresentativeLabel;
		const byUid = sellerCompanyUsers.find(
			(u) => String(u.uid) === String(formik.values.sellerRepresentativeUid),
		);
		return byUid ? `${byUid.firstName} ${byUid.lastName}` : '';
	};

	const selectedContractCountry = CONTRACT_COUNTRIES.find(
		(c) => c.code === formik.values.countryCode,
	);

	return (
		<>
			<SypacText variant="body-regular-medium">
				<p className="text-gray-40 text-[14px] leading-[16px] mx-3 mt-3">
					<T keyName="adminContracts.selectParties">Select parties</T>
				</p>
			</SypacText>

			<div className="flex flex-col gap-3 items-center justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 mx-3 p-[10px]">
				<div ref={countryWrapperRef} className="order-2 relative w-full">
					<SypacInput
						error={!!(formik.touched.countryCode && formik.errors.countryCode)}
						className="w-full"
					>
						<SypacText className="mb-2" variant="overline-normal-large">
							<p>
								<T keyName="adminContracts.productName">Contract country</T>{' '}
								<span className="text-red">*</span>
							</p>
						</SypacText>
						<input
							ref={countryInputRef}
							className={`w-full py-[11px] pl-[42px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
								formik.touched.countryCode && formik.errors.countryCode
									? 'border-red-orange'
									: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
							}`}
							name="countryCode"
							type="text"
							readOnly
							placeholder={t(
								'adminContracts.selectContractCountry',
								'Select contract country',
							)}
							value={
								selectedContractCountry
									? t(
											selectedContractCountry.labelKey,
											selectedContractCountry.fallbackLabel,
									  )
									: formik.values.countryCode || ''
							}
							onClick={() => setShowCountryOptions(true)}
							onFocus={() => setShowCountryOptions(true)}
						/>

						<span
							className={`absolute top-[42px] right-3 flex transform transition-all duration-150 ease-in-out ${
								showCountryOptions ? 'rotate-90' : ''
							}`}
							onClick={() => setShowCountryOptions((s) => !s)}
							role="button"
							aria-label="Toggle contract country options"
						>
							<ChevronRightIcon />
						</span>

						{showCountryOptions && (
							<div
								ref={countryOptionsMenuRef}
								className="absolute left-0 right-0 p-1 top-[70px] flex flex-col w-full max-h-72 bg-white border border-solid border-gray-10 rounded-[10px] overflow-hidden z-50 box-border shadow-2xl"
							>
								{CONTRACT_COUNTRIES.map((c) => (
									<div
										key={c.code}
										className="flex gap-3 items-center p-1 bg-white border border-solid border-white rounded-[8px] cursor-pointer hover:bg-[#FAFAFA] hover:border-[#E8E8E8]"
										onClick={() => pickCountry(c.code)}
									>
										<div className="flex flex-col gap-2">
											<div className="flex items-center gap-3">
												<div className="flex items-start gap-2">
													<span className="mt-[2px]">
														<Flag
															className="object-cover border border-solid border-gray-10 rounded"
															code={c.flagCode}
															width={22}
															height={16}
														/>
													</span>
													<SypacText variant="body-regular-medium">
														<p className="text-gray-80">
															<T keyName={c.labelKey}>{c.fallbackLabel}</T>
														</p>
													</SypacText>
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						)}

						<div className="absolute top-[36px] left-[10px]">
							{selectedContractCountry ? (
								<Flag
									className="object-cover border border-solid border-gray-10 rounded"
									code={selectedContractCountry.flagCode}
									width={22}
									height={16}
								/>
							) : (
								<GlobeIcon color="#A2A2A2" />
							)}
						</div>

						{formik?.touched.countryCode && formik?.errors.countryCode ? (
							<span className="input-error">{formik.errors.countryCode}</span>
						) : null}
					</SypacInput>
				</div>
			</div>

			<div
				ref={buyerWrapperRef}
				className={cn(
					'flex gap-3 justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 mx-3 p-[10px]',
					(formik?.touched?.buyerCompany && formik?.errors?.buyerCompany) ||
						(formik?.touched?.buyerRepresentativeUid &&
							formik?.errors?.buyerRepresentativeUid)
						? 'items-start'
						: 'items-center',
				)}
			>
				<div className="w-full flex flex-col gap-3">
					<SypacInput
						error={
							!!(formik.touched.buyerCompany && formik.errors.buyerCompany)
						}
						className="relative flex-1 min-w-0"
					>
						<SypacText className="mb-2" variant="overline-normal-large">
							<p>
								<T keyName="adminContracts.buyer">Buyer</T>{' '}
								<span className="text-red">*</span>
							</p>
						</SypacText>
						<input
							ref={buyerCompanyInputRef}
							className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
								formik.touched.buyerCompany && formik.errors.buyerCompany
									? 'border-red-orange'
									: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
							}`}
							name="buyerCompanyInput"
							type="text"
							placeholder={t(
								'adminContracts.enterBuyerCompanyName',
								'Enter buyer company name',
							)}
							value={buyerDisplay}
							onChange={handleBuyerInputChange}
							onFocus={handleBuyerFocusOrClick}
							onClick={handleBuyerFocusOrClick}
						/>

						{showBuyerOptions && (
							<div
								ref={buyerOptionsMenuRef}
								className="absolute left-0 right-0 top-[70px] flex flex-col w-full max-h-[224px] bg-white border border-solid border-gray-10 rounded-[10px] overflow-hidden z-50 box-border shadow-2xl"
							>
								{buyerOptions.length === 0 ? (
									<div className="flex items-center justify-center h-[48px]">
										<SypacText variant="body-normal-medium">
											<p className="text-gray-80 text-[14px] leading-[16px]">
												<T keyName="common.noResults">No results</T>
											</p>
										</SypacText>
									</div>
								) : (
									<div className="flex flex-col gap-[3px] w-full p-1 scrollbar-custom scroll-smooth overflow-y-scroll overflow-x-hidden box-border">
										{buyerOptions.map((c) => (
											<div
												key={c.id}
												className="flex gap-3 items-center p-1 bg-white border border-solid border-white rounded-[8px] cursor-pointer hover:bg-[#FAFAFA] hover:border-[#E8E8E8] box-border"
												onClick={() => pickBuyer(c)}
											>
												{c.logoUrl ? (
													<img
														src={c.logoUrl}
														alt="Buyer company logo"
														className="w-[44px] h-[44px] object-cover border border-solid border-gray-10 rounded-[5px] overflow-hidden box-border"
													/>
												) : (
													<div className="w-[44px] h-[44px] flex items-center justify-center object-cover border border-solid border-gray-10 bg-white rounded-[5px] box-border">
														<Person />
													</div>
												)}

												<div className="flex flex-col gap-[6px] items-start">
													<SypacText variant="body-regular-medium">
														<p className="text-[14px] leading-[16px] text-gray-80">
															{c.name}
														</p>
													</SypacText>
													<div className="flex items-start gap-2">
														<Flag
															className="object-cover border border-solid border-gray-10 rounded"
															code={c.countryCode}
															width={19}
															height={14}
														/>
														<SypacText variant="body-regular-medium">
															<p className="text-[12px] leading-[14px] text-gray-40">
																{c.registryAddress}
															</p>
														</SypacText>
													</div>
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						)}

						<div className="absolute top-[36px] left-3">
							<Person color="#A2A2A2" />
						</div>
						{formik?.touched.buyerCompany && formik?.errors.buyerCompany ? (
							<span className="input-error">{formik.errors.buyerCompany}</span>
						) : null}
					</SypacInput>

					<div className="flex items-center gap-2">
						<SypacCheckbox size="md">
							<input
								type="checkbox"
								name="ignoreCreditLimit"
								checked={formik.values.ignoreCreditLimit}
								onChange={(e) =>
									formik.setFieldValue(
										'ignoreCreditLimit',
										e.currentTarget.checked,
									)
								}
								className="cursor-pointer"
							/>
						</SypacCheckbox>
						<SypacText variant="body-regular-medium">
							<p className="text-gray-80 mt-[2px]">
								<T keyName="adminContracts.ignoreCreditLimit">
									Ignore credit limit
								</T>
							</p>
						</SypacText>
					</div>
				</div>

				<SypacInput
					error={
						!!(
							formik.touched.buyerRepresentativeUid &&
							formik.errors.buyerRepresentativeUid
						)
					}
					className="relative w-[194px] shrink-0 self-start"
				>
					<SypacText className="mb-2" variant="overline-normal-large">
						<p>
							<T keyName="adminContracts.companyRepresentative">
								Company representative
							</T>{' '}
							<span className="text-red">*</span>
						</p>
					</SypacText>
					<input
						ref={buyerRepInputRef}
						className={`w-full py-[11px] pl-3 border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
							formik.touched.buyerRepresentativeUid &&
							formik.errors.buyerRepresentativeUid
								? 'border-red-orange'
								: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
						}`}
						name="buyerRepresentativeUid"
						type="text"
						readOnly
						onBlur={formik.handleBlur}
						placeholder={t(
							'adminContracts.selectRepresentative',
							'Select representative',
						)}
						value={resolveBuyerRepName()}
						onClick={() => setShowBuyerReps((s) => !s)}
					/>
					<span
						className={`absolute top-[42px] right-3 flex transform transition-all duration-150 ease-in-out ${
							showBuyerReps ? 'rotate-90' : ''
						}`}
					>
						<ChevronRightIcon />
					</span>

					{showBuyerReps && (
						<div
							ref={buyerRepMenuRef}
							className="absolute left-0 right-0 top-[70px] flex flex-col w-full max-h-72 bg-white border border-solid border-gray-10 rounded-[10px] overflow-hidden z-50 box-border shadow-2xl"
						>
							{!formik.values.buyerCompany ? (
								<div className="flex items-center justify-center h-[48px]">
									<SypacText variant="body-normal-medium">
										<p className="text-gray-80 text-[14px] leading-[16px]">
											<T keyName="adminContracts.searchForBuyerCompany">
												Search for Buyer company
											</T>
										</p>
									</SypacText>
								</div>
							) : (
								<div className="flex flex-col gap-[3px] w-full p-1 scrollbar-custom scroll-smooth overflow-y-scroll overflow-x-hidden box-border">
									{buyerCompanyUsers.map((u) => (
										<div
											key={u.id}
											className="flex gap-[6px] items-center p-1 bg-white border border-solid border-white rounded-[8px] cursor-pointer hover:bg-[#FAFAFA] hover:border-[#E8E8E8] box-border"
											onClick={() => pickBuyerRep(u)}
										>
											{u.photoUrl ? (
												<img
													src={u.photoUrl}
													alt="Buyer representative avatar"
													className="w-[24px] h-[24px] object-cover border border-solid border-gray-10 rounded-[5px] overflow-hidden box-border"
												/>
											) : (
												<div className="w-[24px] h-[24px] flex items-center justify-center border border-solid border-gray-10 bg-white rounded-[5px]">
													<Person />
												</div>
											)}
											<div className="flex flex-col gap-[2px] px-[6px] py-1 pr-3">
												<SypacText variant="body-regular-medium">
													<p className="max-w-[124px] text-gray-80 text-[14px] leading-[16px] truncate">
														{`${u.firstName} ${u.lastName}`}
													</p>
												</SypacText>
												{u.email && (
													<SypacText variant="body-regular-medium">
														<p className="max-w-[124px] text-gray-40 text-[12px] leading-[14px] whitespace-nowrap truncate">
															{u.email}
														</p>
													</SypacText>
												)}
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					)}

					{formik?.touched.buyerRepresentativeUid &&
					formik?.errors.buyerRepresentativeUid ? (
						<span className="input-error">
							{formik.errors.buyerRepresentativeUid}
						</span>
					) : null}
				</SypacInput>
			</div>

			<div
				ref={sellerWrapperRef}
				className={cn(
					'flex gap-3 justify-center bg-alabaster border border-dashed border-gray-10 rounded-10 mx-3 p-[10px]',
					(formik?.touched?.sellerCompany && formik?.errors?.sellerCompany) ||
						(formik?.touched?.sellerRepresentativeUid &&
							formik?.errors?.sellerRepresentativeUid)
						? 'items-start'
						: 'items-center',
				)}
			>
				<SypacInput
					error={
						!!(formik.touched.sellerCompany && formik.errors.sellerCompany)
					}
					className="relative flex-1 min-w-0"
				>
					<SypacText className="mb-2" variant="overline-normal-large">
						<p>
							<T keyName="adminContracts.seller">Seller</T>{' '}
							<span className="text-red">*</span>
						</p>
					</SypacText>
					<input
						ref={sellerCompanyInputRef}
						className={`w-full py-[11px] pl-[35px] border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
							formik.touched.sellerCompany && formik.errors.sellerCompany
								? 'border-red-orange'
								: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
						}`}
						name="sellerCompanyInput"
						type="text"
						placeholder={t(
							'adminContracts.enterSellerCompanyName',
							'Enter seller company name',
						)}
						value={sellerDisplay}
						onChange={handleSellerInputChange}
						onFocus={handleSellerFocusOrClick}
						onClick={handleSellerFocusOrClick}
					/>

					{showSellerOptions && (
						<div
							ref={sellerOptionsMenuRef}
							className="absolute left-0 right-0 top-[70px] flex flex-col w-full max-h-[224px] bg-white border border-solid border-gray-10 rounded-[10px] overflow-hidden z-50 box-border shadow-2xl"
						>
							{sellerOptions.length === 0 ? (
								<div className="flex items-center justify-center h-[48px]">
									<SypacText variant="body-normal-medium">
										<p className="text-gray-80 text-[14px] leading-[16px]">
											<T keyName="common.noResults">No results</T>
										</p>
									</SypacText>
								</div>
							) : (
								<div className="flex flex-col gap-[3px] w-full p-1 scrollbar-custom scroll-smooth overflow-y-scroll overflow-x-hidden box-border">
									{sellerOptions.map((c) => (
										<div
											key={c.id}
											className="flex gap-3 items-center p-1 bg-white border border-solid border-white rounded-[8px] cursor-pointer hover:bg-[#FAFAFA] hover:border-[#E8E8E8] box-border"
											onClick={() => pickSeller(c)}
										>
											{c.logoUrl ? (
												<img
													src={c.logoUrl}
													alt="Seller company logo"
													className="w-[44px] h-[44px] object-cover border border-solid border-gray-10 rounded-[5px] overflow-hidden box-border"
												/>
											) : (
												<div className="w-[44px] h-[44px] flex items-center justify-center border border-solid border-gray-10 bg-white rounded-[5px] box-border">
													<Person />
												</div>
											)}

											<div className="flex flex-col gap-[6px] items-start">
												<SypacText variant="body-regular-medium">
													<p className="text-[14px] leading-[16px] text-gray-80">
														{c.name}
													</p>
												</SypacText>
												<div className="flex items-start gap-2">
													<Flag
														className="object-cover border border-solid border-gray-10 rounded"
														code={c.countryCode}
														width={19}
														height={14}
													/>
													<SypacText variant="body-regular-medium">
														<p className="text-[12px] leading-[14px] text-gray-40">
															{c.registryAddress}
														</p>
													</SypacText>
												</div>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					)}

					<div className="absolute top-[36px] left-[10px]">
						<ProducerCompanyIcon color="#A2A2A2" />
					</div>
					{formik?.touched.sellerCompany && formik?.errors.sellerCompany ? (
						<span className="input-error">{formik.errors.sellerCompany}</span>
					) : null}
				</SypacInput>

				<SypacInput
					error={
						!!(
							formik.touched.sellerRepresentativeUid &&
							formik.errors.sellerRepresentativeUid
						)
					}
					className="relative w-[194px] shrink-0"
				>
					<SypacText className="mb-2" variant="overline-normal-large">
						<p>
							<T keyName="adminContracts.companyRepresentative">
								Company representative
							</T>{' '}
							<span className="text-red">*</span>
						</p>
					</SypacText>
					<input
						ref={sellerRepInputRef}
						className={`w-full py-[11px] pl-3 border rounded-lg placeholder:text-gray-22 placeholder:text-base transition ${
							formik.touched.sellerRepresentativeUid &&
							formik.errors.sellerRepresentativeUid
								? 'border-red-orange'
								: 'border-gray-10 hover:border-gray-30 focus:border-cornflower-blue'
						}`}
						name="sellerRepresentativeUid"
						type="text"
						readOnly
						onBlur={formik.handleBlur}
						placeholder={t(
							'adminContracts.selectRepresentative',
							'Select representative',
						)}
						value={resolveSellerRepName()}
						onClick={() => setShowSellerReps((s) => !s)}
					/>
					<span
						className={`absolute top-[42px] right-3 flex transform transition-all duration-150 ease-in-out ${
							showSellerReps ? 'rotate-90' : ''
						}`}
					>
						<ChevronRightIcon />
					</span>

					{showSellerReps && (
						<div
							ref={sellerRepMenuRef}
							className="absolute left-0 right-0 top-[70px] flex flex-col w-full max-h-72 bg-white border border-solid border-gray-10 rounded-[10px] overflow-hidden z-50 box-border shadow-2xl"
						>
							{!formik.values.sellerCompany ? (
								<div className="flex items-center justify-center h-[48px]">
									<SypacText variant="body-normal-medium">
										<p className="text-gray-80 text-[14px] leading-[16px]">
											<T keyName="adminContracts.searchForSellerCompany">
												Search for Seller company
											</T>
										</p>
									</SypacText>
								</div>
							) : (
								<div className="flex flex-col gap-[3px] w-full p-1 scrollbar-custom scroll-smooth overflow-y-scroll overflow-x-hidden box-border">
									{sellerCompanyUsers.map((u) => (
										<div
											key={u.id}
											className="flex gap-[6px] items-center p-1 bg-white border border-solid border-white rounded-[8px] cursor-pointer hover:bg-[#FAFAFA] hover:border-[#E8E8E8] box-border"
											onClick={() => pickSellerRep(u)}
										>
											{u.photoUrl ? (
												<img
													src={u.photoUrl}
													alt="Seller representative avatar"
													className="w-[24px] h-[24px] object-cover border border-solid border-gray-10 rounded-[5px] overflow-hidden box-border"
												/>
											) : (
												<div className="w-[24px] h-[24px] flex items-center justify-center px-[5px] border border-solid border-gray-10 bg-white rounded-[5px]">
													<Person />
												</div>
											)}
											<div className="flex flex-col gap-[2px] px-[6px] py-1 pr-3">
												<SypacText variant="body-regular-medium">
													<p className="max-w-[124px] text-gray-80 text-[14px] leading-[16px] truncate">
														{`${u.firstName} ${u.lastName}`}
													</p>
												</SypacText>
												{u.email && (
													<SypacText variant="body-regular-medium">
														<p className="max-w-[124px] text-gray-40 text-[12px] leading-[14px] whitespace-nowrap truncate">
															{u.email}
														</p>
													</SypacText>
												)}
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					)}

					{formik?.touched.sellerRepresentativeUid &&
					formik?.errors.sellerRepresentativeUid ? (
						<span className="input-error">
							{formik.errors.sellerRepresentativeUid}
						</span>
					) : null}
				</SypacInput>
			</div>
		</>
	);
};

export default PartiesSection;
